import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api/client";
import type { CompleteTaskResponse, ThisWeekResponse } from "@/lib/api/types";
import { showToast } from "@/components/Toast";

export const tasksKeys = {
  thisWeek: ["tasks", "this-week"] as const,
};

export function useThisWeeksTasks() {
  return useQuery<ThisWeekResponse>({
    queryKey: tasksKeys.thisWeek,
    queryFn: () => apiFetch<ThisWeekResponse>("/api/tasks/this-week"),
  });
}

interface CompleteContext {
  previous: ThisWeekResponse | undefined;
}

export function useCompleteTask() {
  const queryClient = useQueryClient();

  return useMutation<CompleteTaskResponse, ApiError, string, CompleteContext>({
    mutationFn: (taskId) =>
      apiFetch<CompleteTaskResponse>(`/api/tasks/${taskId}/complete`, {
        method: "POST",
      }),
    onMutate: async (taskId) => {
      await queryClient.cancelQueries({ queryKey: tasksKeys.thisWeek });
      const previous = queryClient.getQueryData<ThisWeekResponse>(
        tasksKeys.thisWeek,
      );
      if (previous) {
        queryClient.setQueryData<ThisWeekResponse>(tasksKeys.thisWeek, {
          ...previous,
          tasks: previous.tasks.filter((t) => t.id !== taskId),
        });
      }
      return { previous };
    },
    onError: (_err, _taskId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(tasksKeys.thisWeek, context.previous);
      }
      showToast({
        message: "Couldn't complete task — try again",
        severity: "error",
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: tasksKeys.thisWeek });
    },
  });
}
